// Tappable metric card for report overview
// Shows metric name, score, heatmap thumbnail, and status indicator

import { motion } from 'framer-motion'
import type { MetricDetail } from '@/types/analysis'
import { getStatusColor } from '@/types/analysis'
import { METRIC_INFO } from '@/data/metricExplanations'

interface MetricCardProps {
  metric: MetricDetail
  onClick?: () => void
  isHighlighted?: boolean // For top concerns
}

export function MetricCard({ metric, onClick, isHighlighted = false }: MetricCardProps) {
  const info = METRIC_INFO[metric.key]
  const statusColor = getStatusColor(metric.status)
  const hasHeatmap = Boolean(metric.heatmapUrl)

  // Score display (higher = better skin condition)
  const displayScore = metric.score

  return (
    <motion.button
      onClick={onClick}
      className={`
        relative w-full rounded-xl text-left transition-all overflow-hidden
        ${isHighlighted ? 'border-2 border-teal-200' : 'border border-gray-100'}
        active:scale-[0.98]
      `}
      whileTap={{ scale: 0.98 }}
    >
      {/* Heatmap thumbnail or fallback background */}
      {hasHeatmap ? (
        <div className="relative h-20 w-full bg-gray-100">
          <img
            src={metric.heatmapUrl}
            alt={`${metric.name} heatmap`}
            className="w-full h-full object-cover"
          />
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          {/* Icon overlay */}
          <span className="absolute top-2 left-2 text-xl drop-shadow-lg">{info.icon}</span>
          {/* Status indicator dot */}
          <div
            className="absolute top-2 right-2 w-3 h-3 rounded-full border-2 border-white shadow"
            style={{ backgroundColor: statusColor }}
          />
        </div>
      ) : (
        <div className={`h-20 w-full flex items-center justify-center ${isHighlighted ? 'bg-teal-50' : 'bg-gray-50'}`}>
          <span className="text-3xl opacity-50">{info.icon}</span>
          {/* Status indicator dot */}
          <div
            className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: statusColor }}
          />
        </div>
      )}

      {/* Content area */}
      <div className={`p-3 ${isHighlighted ? 'bg-teal-50' : 'bg-white'}`}>
        {/* Metric name */}
        <p className="text-sm font-medium text-gray-800 mb-1.5">{metric.name}</p>

        {/* Score bar */}
        <div className="mb-1.5">
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: statusColor }}
              initial={{ width: 0 }}
              animate={{ width: `${displayScore}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Score text */}
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">{displayScore}%</span>
          <span className="text-xs font-medium" style={{ color: statusColor }}>
            {metric.status === 'attention' ? 'Needs Care' : metric.status === 'good' ? 'Good' : 'Average'}
          </span>
        </div>
      </div>
    </motion.button>
  )
}

// Simple concern card for Top Concerns - just emoji, name, score
interface TopConcernCardProps {
  metric: MetricDetail
  onClick?: () => void
  rank?: number
}

export function TopConcernCard({ metric, onClick, rank }: TopConcernCardProps) {
  const info = METRIC_INFO[metric.key]
  const statusColor = getStatusColor(metric.status)

  return (
    <motion.button
      onClick={onClick}
      className="relative w-full bg-white rounded-xl p-4 shadow-sm text-left transition-all active:scale-[0.98] border border-gray-100"
      whileTap={{ scale: 0.98 }}
    >
      {/* Rank badge */}
      {rank && (
        <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
          <span className="text-xs font-bold text-gray-500">#{rank}</span>
        </div>
      )}

      <div className="flex flex-col items-center text-center pt-4">
        <span className="text-3xl mb-2">{info.icon}</span>
        <span className="font-medium text-gray-900 text-sm">{metric.name}</span>
        <span className="text-2xl font-bold mt-1" style={{ color: statusColor }}>
          {metric.score}
        </span>
      </div>
    </motion.button>
  )
}

// Large version for 2-per-row display - lighter heatmap like detail popup
interface MetricCardLargeProps {
  metric: MetricDetail
  photoUrl?: string // User's base photo for overlay
  onClick?: () => void
}

export function MetricCardLarge({ metric, photoUrl, onClick }: MetricCardLargeProps) {
  const info = METRIC_INFO[metric.key]
  const hasHeatmap = Boolean(metric.heatmapUrl)

  // Severity label based on skin health score (higher score = better)
  const getSeverityLabel = (score: number) => {
    if (score >= 70) return 'Mild'      // Good condition
    if (score >= 40) return 'Moderate'  // Moderate condition
    return 'Severe'                      // Needs attention
  }

  const getSeverityColor = (score: number) => {
    if (score >= 70) return '#22C55E' // green - good
    if (score >= 40) return '#F59E0B' // amber - moderate
    return '#EF4444' // red - needs attention
  }

  const severityLabel = getSeverityLabel(metric.score)
  const severityColor = getSeverityColor(metric.score)

  // Comparison to average (higher score = better skin)
  const getComparisonLabel = (score: number) => {
    if (score >= 65) return 'Above average'
    if (score >= 45) return 'Near average'
    return 'Below average'
  }

  const getComparisonColor = (score: number) => {
    if (score >= 65) return '#22C55E' // green - better
    if (score >= 45) return '#F59E0B' // amber - average
    return '#EF4444' // red - worse
  }

  const comparisonLabel = getComparisonLabel(metric.score)
  const comparisonColor = getComparisonColor(metric.score)

  return (
    <motion.button
      onClick={onClick}
      className="relative w-full bg-white rounded-xl overflow-hidden shadow-sm text-left transition-all active:scale-[0.98]"
      whileTap={{ scale: 0.98 }}
    >
      {/* Heatmap preview - 3:4 aspect ratio to match cropped photo */}
      <div className="relative aspect-[3/4]">
        {/* Base photo */}
        {photoUrl && (
          <img
            src={photoUrl}
            alt="Your skin"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Heatmap overlay - single layer at 60% opacity like detail popup */}
        {hasHeatmap && (
          <img
            src={metric.heatmapUrl}
            alt={`${metric.name} heatmap`}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity: 0.6,
              mixBlendMode: 'multiply',
            }}
          />
        )}

        {/* Fallback if no photo or heatmap */}
        {!photoUrl && !hasHeatmap && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <span className="text-6xl opacity-30">{info.icon}</span>
          </div>
        )}

        {/* Score badge */}
        <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1">
          <span className="text-white text-sm font-medium">{metric.score}</span>
        </div>
      </div>

      {/* Info - clean white area below image */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-gray-900">{metric.name}</span>
          <span
            className="text-xs font-medium px-2 py-0.5 rounded"
            style={{
              backgroundColor: `${severityColor}20`,
              color: severityColor,
            }}
          >
            {severityLabel}
          </span>
        </div>
        {/* Comparison indicator */}
        <div className="flex items-center gap-1.5">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: comparisonColor }}
          />
          <span className="text-xs text-gray-500">{comparisonLabel}</span>
        </div>
      </div>
    </motion.button>
  )
}

// Compact version for grid display
interface MetricCardCompactProps {
  metric: MetricDetail
  onClick?: () => void
}

export function MetricCardCompact({ metric, onClick }: MetricCardCompactProps) {
  const info = METRIC_INFO[metric.key]
  const statusColor = getStatusColor(metric.status)
  const displayScore = metric.score // Higher = better skin condition
  const hasHeatmap = Boolean(metric.heatmapUrl)

  return (
    <motion.button
      onClick={onClick}
      className="relative flex flex-col items-center justify-center rounded-lg border border-gray-100 min-h-[88px] active:bg-gray-100 overflow-hidden"
      whileTap={{ scale: 0.95 }}
    >
      {/* Status dot */}
      <div
        className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full z-10"
        style={{ backgroundColor: statusColor }}
      />

      {/* Heatmap background or fallback */}
      {hasHeatmap ? (
        <>
          <div className="absolute inset-0">
            <img
              src={metric.heatmapUrl}
              alt={`${metric.name} heatmap`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          </div>
          <span className="relative text-lg mb-0.5 drop-shadow-lg">{info.icon}</span>
          <span className="relative text-[10px] text-white text-center leading-tight drop-shadow">{metric.name}</span>
          <span className="relative text-xs font-bold mt-0.5 text-white drop-shadow">
            {displayScore}%
          </span>
        </>
      ) : (
        <div className="bg-gray-50 p-2 flex flex-col items-center justify-center w-full h-full">
          <span className="text-lg mb-0.5">{info.icon}</span>
          <span className="text-[10px] text-gray-600 text-center leading-tight">{metric.name}</span>
          <span className="text-xs font-semibold mt-0.5" style={{ color: statusColor }}>
            {displayScore}%
          </span>
        </div>
      )}
    </motion.button>
  )
}
